import React from 'react';
import { StressRecommendationType } from '../../../views/StressInsights';
import { Box, Typography } from '@mui/material';
import { CustomIcon } from '../../../atoms';
import { RootineImage } from '../../../atoms/RootineImage';
import RootineButton from '../../../atoms/RootineButton';
import { colors } from '../../../utils/theme';
import { useGlobalState } from '../../../utils/GlobalStateProvider';
import { Product } from '../../../cms/types';

export const StressRecommendationDetails = ({
  recommendationType,
}: {
  recommendationType: StressRecommendationType;
}): JSX.Element => {
  const { state, addToCart } = useGlobalState();
  const blackFillIcons = [
    'saltShaker',
    'banana',
    'veg',
    'coffeeMug',
    'cocktail',
    'weights',
    'drinkWater',
    'bed',
    'spa',
    'calm',
    'travelExplore',
    'crossedMeal',
    'fishMeal',
    'spicy',
    'iceCream02',
    'socialCircle',
    'celebration',
  ];

  const getProduct = (title: string) => {
    return state.products.size > 0 ? state.products.get(title) : undefined;
  };

  const processDescription = (description: string): React.ReactNode[] => {
    const keywords = ['Stress', 'Support', 'Unwind', 'Focus', 'Sleep'];

    return description
      .split(/\s+/)
      .map((word, index) => {
        // If the word is all uppercase or is one of the keywords
        if (word === word.toUpperCase() || keywords.includes(word)) {
          return (
            <Typography key={index} variant="bold">
              {word}
            </Typography>
          );
        }
        return word;
      })
      .reduce<React.ReactNode[]>(
        (prev, current) => [...prev, ' ', current],
        []
      );
  };

  const handleAddProduct = (product: Product) => {
    addToCart(product);
  };

  const isProductInCart = (productCode: string) => {
    return !!state.cart?.find((item) => item.code === productCode);
  };

  return (
    <>
      {recommendationType.name === 'supplements' ? (
        <Box display="flex" flexDirection="column">
          <Typography variant="light" fontSize={14} sx={{ mt: 1, mb: 3 }}>
            {recommendationType.description}
          </Typography>

          {recommendationType.recs.map((rec, index) => {
            const product = getProduct(rec.title);

            return (
              <Box
                display="flex"
                flexDirection="column"
                sx={{ mt: 3, mb: index === 0 ? 3 : 0 }}
                key={`${rec.title}-${index}`}
              >
                {product && (
                  <>
                    <Typography variant="light" fontSize={14}>
                      {processDescription(rec.description)}
                    </Typography>
                    <Box display="flex" flexDirection="row" sx={{ my: 3 }}>
                      {product?.heroImage[0].src && (
                        <Box>
                          <RootineImage
                            src={product?.heroImage[0].src}
                            size="sm"
                            alt={`${recommendationType.name}-image`}
                            fit="cover"
                          />
                        </Box>
                      )}

                      <Box
                        display="flex"
                        flexDirection="column"
                        sx={{ ml: 3, width: '60%' }}
                      >
                        <Typography
                          variant="medium"
                          fontSize={18}
                          sx={{ mb: 1 }}
                        >
                          {product?.title}
                        </Typography>
                        <Typography variant="light" fontSize={14}>
                          Ingredients: {rec?.secondary_description}
                        </Typography>
                      </Box>
                    </Box>

                    <RootineButton
                      variation="primary"
                      size="medium"
                      label={
                        isProductInCart(product.code)
                          ? 'In your cart'
                          : `${
                              product?.isPreorder
                                ? 'Pre-order now'
                                : 'Add to cart'
                            } - $${product?.price?.toFixed(2)}`
                      }
                      onClick={() => handleAddProduct(product)}
                      disabled={isProductInCart(product.code)}
                    />
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column">
          <Typography variant="light" fontSize={14} sx={{ mt: 1 }}>
            {recommendationType.description}
          </Typography>
          {recommendationType.recs.map((rec, index) => (
            <Box
              display="flex"
              flexDirection="column"
              key={`${recommendationType.name}-${index}`}
            >
              <Box
                display="flex"
                alignItems="center"
                mt={index === 0 ? 3 : 6}
                mb={1.5}
              >
                {rec.icon && (
                  <CustomIcon
                    key={`${rec.icon}-${index}`}
                    name={rec.icon}
                    hex="black"
                    sx={{
                      width: 22,
                      height: 22,
                      fill: blackFillIcons.includes(rec.icon)
                        ? colors.greys.BRAND_BLACK
                        : colors.greys.BASE_WHITE,
                      mr: 0.75,
                    }}
                  />
                )}

                <Typography variant="medium" fontSize={18}>
                  {rec.title}
                </Typography>
              </Box>

              {rec.imgSrc && (
                <RootineImage
                  src={rec.imgSrc}
                  height={260}
                  alt={`${rec.title}-image`}
                  fit="cover"
                />
              )}

              {rec.goal && (
                <Typography variant="medium" fontSize={18} sx={{ my: 1.5 }}>
                  Goal: {rec.goal}
                </Typography>
              )}

              <Typography variant="light" fontSize={14}>
                {rec.description}
              </Typography>
            </Box>
          ))}

          {/* Commenting out until we have a product option in Shopify */}
          {/* <Typography variant="medium" fontSize={16} sx={{ mt: 4, mb: 3 }}>
            Have more questions? Want to dive deeper into your results?
          </Typography>
          <RootineButton
            variation="primary"
            hideIcon
            label="Book 1:1 consult with our Clinician"
            size="medium"
            as="a"
            href={`${CHECKOUT_URLS.coaching.link}`}
          /> */}
        </Box>
      )}
    </>
  );
};
